import { useRef, useState } from "react";
import { FiMic, FiSquare, FiTrash2, FiRadio } from "react-icons/fi";
import { PiSmileyFill } from "react-icons/pi";

import { useMediaRecorder } from "../../lib/useAudioRecorder";
import AudioVisualizer from "../../lib/AudioVisualizer";
import Media from "../shared/Media";
import PostButton from "../PostButton";
import { createPost } from "../../api";
import ToastMessage from "../ToastMessage";
import EmojiReactPicker from "../EmojiReactPicker";

const Voice = ({ close }) => {
  const [caption, setCaption] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uniqueIdRef = useRef(`wavesurfer-${Date.now()}`);
  const smileyRef = useRef(null);

  const { mediaUrl, link, startRecording, stopRecording, deleteRecording, recordingState, analyser } = useMediaRecorder();

  const type = "voice";
  const maxCaptionLength = 500;

  const handleDelete = () => {
    deleteRecording();
    setCaption("");
    setShowEmoji(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!mediaUrl || !link) {
      ToastMessage("Please record something before posting.");
      return;
    }

    if (isSubmitting) return;

    const fileExtension = link.type.split("/")[1]?.split(";")[0] || "webm";
    const audioFile = new File([link], `audio_file.${fileExtension}`, { type: link.type });
    const formData = new FormData();

    formData.append("caption", caption.trim());
    formData.append("type", type);
    formData.append("content", audioFile);

    try {
      setIsSubmitting(true);

      const res = await createPost(formData);

      if (res?.data?.message) {
        ToastMessage(res.data.message);

        setCaption("");
        setShowEmoji(false);
        deleteRecording();

        close?.();
      }
    } catch (err) {
      console.error(err);
      ToastMessage("Failed to submit your audio post.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit}>
        {/* ================= HEADER ================= */}

        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
              <FiRadio size={20} />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900">Voice post</h3>
              <p className="mt-0.5 text-xs text-gray-400">Record and share your voice</p>
            </div>
          </div>

          {recordingState === "recording" && (
            <div className="flex items-center gap-2 rounded-full bg-red-50 px-3 py-1.5">
              <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
              <span className="text-[11px] font-semibold text-red-600">Recording</span>
            </div>
          )}
        </div>

        {/* ================= RECORDER ================= */}

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          {/* Empty / Recording State */}

          {!mediaUrl && (
            <div className="flex min-h-[220px] flex-col items-center justify-center px-6 py-8 text-center">
              {/* Mic */}

              <div className={`flex h-20 w-20 items-center justify-center rounded-full transition-all duration-300 ${recordingState === "recording" ? "bg-red-50 text-red-500 ring-8 ring-red-50/70" : "bg-orange-50 text-orange-600"}`}>
                {recordingState === "recording" ? <FiRadio size={31} /> : <FiMic size={30} />}
              </div>

              {/* Title */}

              <h4 className="mt-5 text-base font-semibold text-gray-900">{recordingState === "recording" ? "Recording your voice..." : "Ready to record?"}</h4>

              <p className="mt-1.5 max-w-[300px] text-xs leading-5 text-gray-400">
                {recordingState === "recording" ? "Speak naturally. Your audio is being captured." : "Tap the microphone and start sharing what's on your mind."}
              </p>

              {/* Visualizer */}

              {recordingState === "recording" && analyser && (
                <div className="mt-5 w-full max-w-sm">
                  <AudioVisualizer analyser={analyser} isPlaying={recordingState === "recording"} mediaUrl={null} />
                </div>
              )}

              {/* Controls */}

              <div className="mt-6">
                {recordingState === "idle" && (
                  <button type="button" onClick={startRecording} className="flex items-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-orange-100 transition-all duration-200 hover:bg-orange-600 hover:shadow-md active:scale-[0.97]">
                    <FiMic size={18} />
                    Start recording
                  </button>
                )}

                {recordingState === "recording" && (
                  <button type="button" onClick={stopRecording} className="flex items-center gap-2 rounded-xl bg-red-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-red-100 transition-all duration-200 hover:bg-red-600 active:scale-[0.97]">
                    <FiSquare size={16} />
                    Stop recording
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ================= RECORDED AUDIO ================= */}

          {mediaUrl && (
            <div className="p-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                    <FiMic size={16} />
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-800">Recording ready</p>
                    <p className="mt-0.5 text-[11px] text-gray-400">Preview your audio before posting</p>
                  </div>
                </div>

                <span className="rounded-full bg-green-50 px-2.5 py-1 text-[10px] font-semibold text-green-600">Ready</span>
              </div>

              {/* Audio Preview */}

              <div className="mt-4 flex items-center gap-3 rounded-xl bg-gray-50/70 p-3">
                <div className="min-w-0 flex-1">
                  <Media media={mediaUrl} container={uniqueIdRef.current} />
                </div>

                <button type="button" onClick={handleDelete} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-gray-400 shadow-sm ring-1 ring-gray-100 transition-all hover:bg-red-50 hover:text-red-500 hover:ring-red-100 active:scale-95" aria-label="Delete recording">
                  <FiTrash2 size={17} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ================= CAPTION ================= */}

        {mediaUrl && (
          <div className="mt-5 overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-200 focus-within:border-orange-300 focus-within:ring-4 focus-within:ring-orange-50">
            <div className="relative">
              <label htmlFor="voice-caption" className="sr-only">Voice post caption</label>

              <textarea
                id="voice-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={4}
                maxLength={maxCaptionLength}
                placeholder="Add a caption..."
                className="min-h-[105px] w-full resize-none border-none bg-white px-4 py-3.5 pr-12 text-sm leading-6 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0"
              />

              {/* Emoji */}

              <button
                ref={smileyRef}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEmoji((prev) => !prev);
                }}
                className={`absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-xl transition-all ${showEmoji ? "bg-orange-50 text-orange-600" : "bg-gray-50 text-gray-400 hover:bg-orange-50 hover:text-orange-600"}`}
              >
                <PiSmileyFill size={20} />
              </button>

              <EmojiReactPicker set={setCaption} showEmoji={showEmoji} setShowEmoji={setShowEmoji} triggerRef={smileyRef} />
            </div>

            {/* Caption Footer */}

            <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/50 px-4 py-2">
              <span className="text-[11px] text-gray-400">Caption is optional</span>
              <span className={`text-[11px] font-medium ${caption.length > maxCaptionLength * 0.9 ? "text-orange-500" : "text-gray-400"}`}>{caption.length}/{maxCaptionLength}</span>
            </div>
          </div>
        )}

        {/* ================= POST ACTION ================= */}

        <div className="mt-4 flex items-center justify-between">
          {mediaUrl && <p className="hidden text-xs text-gray-400 sm:block">Your recording is ready to publish</p>}

          <div className={`ml-auto transition-all duration-200 ${mediaUrl ? "opacity-100" : "pointer-events-none opacity-50"}`}>
            <PostButton type="submit" disabled={!mediaUrl || !link || isSubmitting} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default Voice;